import {
  Box,
  Heading,
  Text,
  Divider,
  Skeleton,
  Flex,
  useToast,
  Link,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  SettingsBreadcrumb,
  SettingsFacilitiesBreadcrumb,
} from './SettingsBreadcrumbs';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';

type SettingsFacilityPortfolioCovenantsTemplateProps = {
  creditFacilityId: string;
  children: React.ReactNode;
};
export const SettingsFacilityPortfolioCovenantsTemplate = ({
  creditFacilityId,
  children,
}: SettingsFacilityPortfolioCovenantsTemplateProps) => {
  const toast = useToast();
  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
  });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-portfolio-covenants-template-loading"
      />
    );
  }

  const creditFacilityName = commonUtils.creditFacilitySlugToName(
    creditFacilityData.creditFacility.slug,
  );

  const breadcrumbs = [
    SettingsBreadcrumb,
    SettingsFacilitiesBreadcrumb,
    { label: creditFacilityName },
    { label: 'Portfolio Covenants' },
  ];

  return (
    <Box p="40px">
      <Flex>
        <Flex direction="column" flexGrow={1} flexShrink={0}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <Heading as="h1" mt="16px" mb="16px" fontSize="36px" fontWeight={600}>
            Portfolio Covenants
          </Heading>
          <Text fontSize="16px" mb="16px" color={colors.black[70]}>
            Manage portfolio covenants for{' '}
            <Text as="span" fontSize="16px" mb="16px" fontWeight={500}>
              {`${creditFacilityName}.`}
            </Text>
          </Text>
        </Flex>
        <Flex direction="column">
          <Link
            color={colors.gray[500]}
            fontSize="0.75rem"
            as={RouterLink}
            to="./../../borrowing-base/configuration"
          >
            Go to Borrowing Base Settings
          </Link>
        </Flex>
      </Flex>
      <Divider />

      <Box mt="32px">{children}</Box>
    </Box>
  );
};
